import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { 
  Search, Plus, Phone, 
  Mail, MessageSquare, MoreVertical, Flame,
  Snowflake, ThermometerSun, X, CheckCircle2,
  Save, Smile, Meh, Frown, Loader2,
  UserPlus, FileText, History,
  XCircle, Wallet, ArrowLeft, Users, Calendar as CalendarIcon, ChevronDown
} from 'lucide-react';
import Avatar from '../../../components/Avatar/Avatar';
import ERPSelectSearchModal, { ERPSelectModalItem } from '../../../components/ERP/Modal/ERPSelectSearchModal';
import { CalendarRangePicker } from '../../../components/ERP/Calendar/Calendar';
import { leadService, customerService } from '../../../services/erpApi';
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions';
import '../Financeiro/Dashboard/FinanceDashboardPage.css';
import '../Plantao/DutyRosterPage.css';
import './ProspectingPage.css';

interface Lead {
  id: string;
  name: string;
  institution: string;
  specialty: string;
  temperature: 'cold' | 'warm' | 'hot';
  lastContact: string;
  status: string;
  score?: number;
  createdBy: {
    name: string;
    avatar: string;
  };
  assignedTo: number | null;
  createdDay: string;
}

const LEAD_SECTORS = [
  'Academia / Estúdio de Pilates',
  'Ambulância / Serviço de Remoção',
  'Casa de Repouso / ILPI',
  'Centro de Reabilitação / Fisioterapia',
  'Cozinha Industrial / Restaurante',
  'Creche / Escola',
  'Distribuidora / Revenda',
  'Estúdio de Tatuagem / Piercing',
  'Frigorífico / Indústria Alimentícia',
  'Hospital',
  'Home Care / Atendimento Domiciliar',
  'Indústria (Geral) / Segurança do Trabalho (SST)',
  'Laboratório de Análises Clínicas',
  'Medicina do Trabalho',
  'Órgão Público / Prefeitura / SAMU',
  'Pet Shop / Banho e Tosa',
  'Podologia / Estética',
  'Profissional Liberal / Pessoa Física'
];

const HEAT_OPTIONS = [
  { id: '', label: 'Todos', detail: 'Frio, morno e quente' },
  { id: 'cold', label: 'Frio', detail: 'Ainda sem avanço' },
  { id: 'warm', label: 'Morno', detail: 'Em conversa' },
  { id: 'hot', label: 'Quente', detail: 'Perto de fechar' }
];

function leadDay(value: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function dayInPeriod(day: string, from: string, to: string) {
  if (!from && !to) return true;
  if (!day) return false;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

interface WalletHolder {
  id: number;
  name: string;
  email?: string;
  avatar_url?: string;
  access_group_name?: string;
}

const ProspectingPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // States para Modais
  const [isInteractionModalOpen, setIsInteractionModalOpen] = useState(false);
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false);
  const [isCancelLeadModalOpen, setIsCancelLeadModalOpen] = useState(false);
  const [leadToCancel, setLeadToCancel] = useState<Lead | null>(null);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [interactionType, setInteractionType] = useState<'phone' | 'message' | 'email'>('phone');
  const [interactionNote, setInteractionNote] = useState('');
  const [satisfaction, setSatisfaction] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [customerFound, setCustomerFound] = useState<any>(null);
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    institution: '',
    specialty: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    cellphone: ''
  });

  // Lista de Leads
  const [leads, setLeads] = useState<Lead[]>([]);
  const [holders, setHolders] = useState<WalletHolder[]>([]);
  const [openWalletId, setOpenWalletId] = useState<number | 'loose' | null>(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [isRedistributeOpen, setIsRedistributeOpen] = useState(false);
  const [recipientIds, setRecipientIds] = useState<number[]>([]);
  const [redistributeError, setRedistributeError] = useState('');
  const [heat, setHeat] = useState('');
  const [heatOpen, setHeatOpen] = useState(false);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sectorOpen, setSectorOpen] = useState(false);
  const [sectorQuery, setSectorQuery] = useState('');
  const calendarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (target instanceof Element && target.closest('.prospecting-lead-menu-button')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!openDropdownId) return undefined;
    const close = () => setOpenDropdownId(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openDropdownId]);

  const openLeadMenu = (event: React.MouseEvent<HTMLButtonElement>, leadId: string) => {
    event.stopPropagation();
    if (openDropdownId === leadId) {
      setOpenDropdownId(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 224;
    const height = 168;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const below = rect.bottom + 8;
    const top = below + height > window.innerHeight - 8 ? Math.max(8, rect.top - height - 8) : below;
    setMenuPos({ top, left });
    setOpenDropdownId(leadId);
  };

  // Carregar leads do backend
  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      setLoadingLeads(true);
      const [response, wallets] = await Promise.all([
        leadService.getAll({ status: '!cancelado', limit: 1000 }),
        leadService.wallets()
      ]);
      if (wallets.success) setHolders(wallets.data || []);
      if (response.success) {
        const formattedLeads = response.data.map((lead: any) => ({
            id: lead.id.toString(),
            name: lead.name,
            institution: lead.institution || '',
            specialty: lead.specialty || '',
            temperature: lead.temperature || 'cold',
            score: lead.score || 0,
            lastContact: lead.last_contact_date 
              ? new Date(lead.last_contact_date).toLocaleDateString('pt-BR')
              : 'Nunca',
            status: lead.status || 'novo',
            assignedTo: lead.assigned_to ? Number(lead.assigned_to) : null,
            createdDay: leadDay(lead.created_at),
            createdBy: {
              name: lead.created_by_name || 'Sistema',
              avatar: lead.created_by_avatar || ''
            }
          }));
        setLeads(formattedLeads);
      }
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (!isAddLeadModalOpen) return undefined;
    const digits = leadFormData.cpf_cnpj.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) {
      setCustomerFound(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSearchingCustomer(true);
        const response = await leadService.lookupCustomer(digits);
        const customer = response.success ? response.data : null;
        if (cancelled) return;
        setCustomerFound(customer || null);
        if (customer) {
          setLeadFormData((prev) => ({
            ...prev,
            name: customer.name || prev.name,
            institution: customer.name || prev.institution,
            email: customer.email || prev.email,
            phone: customer.phone || prev.phone,
            cellphone: customer.cellphone || prev.cellphone
          }));
        }
      } catch (error) {
        if (!cancelled) setCustomerFound(null);
      } finally {
        if (!cancelled) setSearchingCustomer(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAddLeadModalOpen, leadFormData.cpf_cnpj]);

  const getTempIcon = (temp: string) => {
    switch (temp) {
      case 'hot': return <Flame size={14} />;
      case 'warm': return <ThermometerSun size={14} />;
      default: return <Snowflake size={14} />;
    }
  };

  const handleSaveInteraction = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activeLead || !interactionNote.trim()) {
      return;
    }

    try {
      setIsSaving(true);

      const response = await leadService.createInteraction(activeLead.id, {
        type: interactionType,
        content: interactionNote.trim(),
        satisfaction: satisfaction || null,
        subject: null,
        next_action: null
      });

      if (response.success) {
        setSaveSuccess(true);
        // Limpar campos
        setInteractionNote('');
        setSatisfaction(null);
        setInteractionType('phone');
        
        // Fechar modal após 2 segundos
        setTimeout(() => {
          setIsInteractionModalOpen(false);
          setSaveSuccess(false);
          setActiveLead(null);
        }, 2000);
      } else {
        throw new Error(response.message || 'Erro ao salvar interação');
      }
    } catch (error: any) {
      console.error('Erro ao salvar interação:', error);
      alert('Erro ao salvar interação: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    if (!String(formData.get('specialty') || '').trim()) {
      alert('Escolha o setor do lead.');
      return;
    }
    setIsSaving(true);
    
    try {
      const cpfCnpj = (formData.get('cpf_cnpj') as string)?.replace(/\D/g, '') || '';
      
      // Criar cliente se não existir e CPF/CNPJ foi informado
      if (cpfCnpj && !customerFound) {
        try {
          const customerData = {
            name: (formData.get('name') as string) || (formData.get('institution') as string),
            cpfCnpj: cpfCnpj,
            email: (formData.get('email') as string) || null,
            phone: (formData.get('phone') as string) || null,
            cellphone: (formData.get('cellphone') as string) || null,
            type: cpfCnpj.length === 11 ? 'F' : 'J', // F = CPF, J = CNPJ
            status: 'active'
          };
          
          await customerService.create(customerData);
        } catch (error: any) {
          // Se cliente já existe, buscar pelo CPF/CNPJ
          if (error.response?.status === 400) {
            await customerService.getAll({ search: cpfCnpj });
          }
        }
      }

      const leadData = {
        name: formData.get('name') as string,
        institution: formData.get('institution') as string,
        specialty: formData.get('specialty') as string,
        email: formData.get('email') as string || null,
        phone: formData.get('phone') as string || null,
        cellphone: formData.get('cellphone') as string || null,
        cpf_cnpj: cpfCnpj || null,
        temperature: 'cold',
        status: 'novo'
      };

      const response = await leadService.create(leadData);
      
      if (response.success) {
        // Recarregar leads
        await loadLeads();
        setIsAddLeadModalOpen(false);
        // Resetar formulário
        setLeadFormData({
          name: '',
          institution: '',
          specialty: '',
          cpf_cnpj: '',
          email: '',
          phone: '',
          cellphone: ''
        });
        setCustomerFound(null);
      } else {
        alert(response.message || 'Erro ao criar lead');
      }
    } catch (error: any) {
      console.error('Erro ao criar lead:', error);
      alert(error.response?.data?.message || 'Erro ao criar lead');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelLead = async () => {
    if (!leadToCancel) return;
    
    // Verificar permissão antes de cancelar
    if (!hasPermission(MODULES.PROSPECTING, ACTIONS.PROSPECTING_UPDATE)) {
      alert('Você não tem permissão para cancelar leads.');
      return;
    }
    
    try {
      setIsSaving(true);
      const response = await leadService.cancel(leadToCancel.id);
      
      if (response.success) {
        // Recarregar leads
        await loadLeads();
        setIsCancelLeadModalOpen(false);
        setLeadToCancel(null);
      } else {
        alert(response.message || 'Erro ao cancelar lead');
      }
    } catch (error: any) {
      console.error('Erro ao cancelar lead:', error);
      alert(error.response?.data?.message || 'Erro ao cancelar lead');
    } finally {
      setIsSaving(false);
    }
  };

  const openCancelModal = (lead: Lead) => {
    setLeadToCancel(lead);
    setIsCancelLeadModalOpen(true);
    setOpenDropdownId(null); // Fechar dropdown
  };

  const canRedistribute = hasPermission(MODULES.PROSPECTING, ACTIONS.PROSPECTING_REDISTRIBUTE);
  const holderIds = new Set(holders.map((holder) => Number(holder.id)));
  const filteredLeads = leads.filter((lead) => (
    (!heat || lead.temperature === heat) && dayInPeriod(lead.createdDay, periodFrom, periodTo)
  ));
  const looseLeads = filteredLeads.filter((lead) => !lead.assignedTo || !holderIds.has(lead.assignedTo));
  const walletQuery = searchTerm.trim().toLowerCase();
  const visibleHolders = holders.filter((holder) =>
    `${holder.name} ${holder.email || ''} ${holder.access_group_name || ''}`.toLowerCase().includes(walletQuery)
  );
  const openHolder = typeof openWalletId === 'number'
    ? holders.find((holder) => Number(holder.id) === openWalletId) || null
    : null;
  const walletLeads = (openWalletId === 'loose'
    ? looseLeads
    : filteredLeads.filter((lead) => lead.assignedTo === openWalletId)
  ).filter((lead) =>
    lead.name.toLowerCase().includes(walletQuery) ||
    lead.institution.toLowerCase().includes(walletQuery) ||
    lead.specialty.toLowerCase().includes(walletQuery)
  );
  const allWalletSelected = walletLeads.length > 0 && walletLeads.every((lead) => selectedLeadIds.includes(lead.id));

  const openWallet = (id: number | 'loose') => {
    setOpenWalletId(id);
    setSelectedLeadIds([]);
    setSearchTerm('');
  };

  const toggleLead = (id: string) => {
    setSelectedLeadIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const toggleRecipient = (id: number) => {
    setRecipientIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const handleRedistribute = async () => {
    if (!selectedLeadIds.length || !recipientIds.length) {
      setRedistributeError('Escolha as leads e quem vai recebê-las.');
      return;
    }
    try {
      setIsSaving(true);
      setRedistributeError('');
      const response = await leadService.redistribute(selectedLeadIds, recipientIds);
      if (!response.success) {
        setRedistributeError(response.message || 'Não foi possível redistribuir.');
        return;
      }
      setIsRedistributeOpen(false);
      setRecipientIds([]);
      setSelectedLeadIds([]);
      await loadLeads();
    } catch (error: any) {
      setRedistributeError(error.response?.data?.message || 'Não foi possível redistribuir.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="finance-dashboard prospecting-page">
      <div className="duty-roster-toolbar">
        <div>
          <h1 className="prospecting-title">Hub de <span className="prospecting-title-accent">Prospecção</span></h1>
          <p className="prospecting-subtitle">Carteiras por pessoa</p>
        </div>
      </div>

      {/* Leads Grid */}
      {loadingLeads ? (
        <div className="prospecting-loading-state">
          <Loader2 size={32} />
          <p>Carregando carteiras...</p>
        </div>
      ) : openWalletId ? (
        <div className="patients-table-card prospecting-wallet-table">
          <div className="patients-table-head">
            <div>
              <h3>{openHolder ? openHolder.name : 'Sem carteira'}</h3>
              <p className="patients-muted">
                {openHolder
                  ? `${openHolder.access_group_name || 'Grupo de acesso'} · ${walletLeads.length} lead${walletLeads.length === 1 ? '' : 's'}`
                  : `${walletLeads.length} lead${walletLeads.length === 1 ? '' : 's'} sem responsável com grupo ativo`}
              </p>
            </div>
            <div className="prospecting-wallet-head-actions">
              <button type="button" className="prospecting-btn prospecting-btn-primary" onClick={() => setIsAddLeadModalOpen(true)}>
                <Plus size={16} /> Novo Lead
              </button>
              {canRedistribute && (
                <button
                  type="button"
                  className="prospecting-btn prospecting-btn-primary"
                  disabled={!selectedLeadIds.length}
                  onClick={() => {
                    setRecipientIds([]);
                    setRedistributeError('');
                    setIsRedistributeOpen(true);
                  }}
                >
                  <Users size={16} /> Redistribuir{selectedLeadIds.length ? ` (${selectedLeadIds.length})` : ''}
                </button>
              )}
              <button type="button" className="prospecting-btn" onClick={() => { setOpenWalletId(null); setSelectedLeadIds([]); setSearchTerm(''); setHeat(''); setPeriodFrom(''); setPeriodTo(''); setCalendarOpen(false); setHeatOpen(false); }}>
                <ArrowLeft size={16} /> Carteiras
              </button>
            </div>
          </div>
          <div className="duty-roster-filter-row prospecting-filter-row">
            <div className="prospecting-filter-search">
              <Search size={14} className="prospecting-filter-search-icon" />
              <input
                className="duty-roster-search"
                placeholder="Buscar lead"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              type="button"
              ref={calendarRef}
              className={`duty-roster-icon-btn ${periodFrom || periodTo ? 'on' : ''}`}
              title={periodFrom ? `${periodFrom.split('-').reverse().join('/')} a ${(periodTo || periodFrom).split('-').reverse().join('/')}` : 'Filtrar período'}
              aria-label="Filtrar período"
              aria-expanded={calendarOpen}
              onClick={() => { setCalendarOpen((open) => !open); setHeatOpen(false); }}
            >
              <CalendarIcon size={16} />
            </button>
            <CalendarRangePicker
              open={calendarOpen}
              onOpenChange={setCalendarOpen}
              anchorRef={calendarRef}
              startValue={periodFrom || null}
              endValue={periodTo || null}
              title="Filtrar leads"
              subtitle="Data de cadastro"
              selectSingleDay
              onRangeChange={(start, end) => {
                const from = start || '';
                setPeriodFrom(from);
                setPeriodTo(end || from);
              }}
              onClear={() => {
                setPeriodFrom('');
                setPeriodTo('');
              }}
            />
            <div className="duty-roster-pop">
              {heatOpen && (
                <button type="button" className="duty-roster-pop-backdrop" aria-label="Fechar calor" onClick={() => setHeatOpen(false)} />
              )}
              <button
                type="button"
                className={`duty-roster-icon-btn ${heat ? 'on' : ''}`}
                title={HEAT_OPTIONS.find((item) => item.id === heat)?.label || 'Calor'}
                aria-label="Filtrar calor"
                aria-pressed={Boolean(heat)}
                onClick={() => { setHeatOpen((open) => !open); setCalendarOpen(false); }}
              >
                <Flame size={16} />
              </button>
              {heatOpen && (
                <div className="duty-roster-menu" role="menu">
                  <p>Calor</p>
                  {HEAT_OPTIONS.map((item) => (
                    <button
                      key={item.id || 'all'}
                      type="button"
                      className={item.id === heat ? 'active' : ''}
                      onClick={() => { setHeat(item.id); setHeatOpen(false); }}
                    >
                      {item.label}
                      <small>{item.detail}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {walletLeads.length === 0 ? (
            <p className="prospecting-wallet-empty">Nenhuma lead nesta carteira.</p>
          ) : (
            <div className="patients-table-scroll">
              <table className="patients-table">
                <thead>
                  <tr>
                    {canRedistribute && (
                      <th>
                        <input
                          type="checkbox"
                          checked={allWalletSelected}
                          onChange={() => setSelectedLeadIds(allWalletSelected ? [] : walletLeads.map((lead) => lead.id))}
                          aria-label="Selecionar todas as leads"
                        />
                      </th>
                    )}
                    <th>Lead</th>
                    <th>Instituição</th>
                    <th>Especialidade</th>
                    <th>Temperatura</th>
                    <th>Status</th>
                    <th>Último contato</th>
                    <th>Score</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {walletLeads.map((lead) => (
                    <tr key={lead.id}>
                      {canRedistribute && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.includes(lead.id)}
                            onChange={() => toggleLead(lead.id)}
                            aria-label={`Selecionar ${lead.name}`}
                          />
                        </td>
                      )}
                      <td className="is-strong">
                        <button type="button" className="prospecting-lead-link" onClick={() => navigate(`/erp/prospecting/contacts/${lead.id}`)}>
                          {lead.name}
                        </button>
                      </td>
                      <td>{lead.institution || '—'}</td>
                      <td>{lead.specialty || '—'}</td>
                      <td>
                        <span className={`prospecting-temperature-badge ${
                          lead.temperature === 'hot' ? 'prospecting-temperature-hot' : lead.temperature === 'warm' ? 'prospecting-temperature-warm' : 'prospecting-temperature-cold'
                        }`}>
                          {getTempIcon(lead.temperature)}
                          {lead.temperature === 'hot' ? 'Quente' : lead.temperature === 'warm' ? 'Morno' : 'Frio'}
                        </span>
                      </td>
                      <td>{lead.status}</td>
                      <td>{lead.lastContact}</td>
                      <td>{lead.score || 0}/10</td>
                      <td className="is-right">
                        <div className="prospecting-lead-actions">
                          <div className="prospecting-lead-dropdown">
                            <button
                              type="button"
                              onClick={(event) => openLeadMenu(event, lead.id)}
                              className={`prospecting-lead-menu-button ${openDropdownId === lead.id ? 'active' : ''}`}
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="prospecting-wallet-grid">
          {visibleHolders.map((holder) => {
            const count = leads.filter((lead) => lead.assignedTo === Number(holder.id)).length;
            return (
              <button key={holder.id} type="button" className="prospecting-wallet-card" onClick={() => openWallet(Number(holder.id))}>
                <Avatar name={holder.name} email={holder.email || ''} imageUrl={holder.avatar_url} size="lg" />
                <div className="prospecting-wallet-info">
                  <strong>{holder.name}</strong>
                  {holder.email ? <span>{holder.email}</span> : null}
                  <em>{holder.access_group_name || 'Grupo de acesso'}</em>
                </div>
                <span className="prospecting-wallet-count">
                  <Wallet size={14} />
                  {count} lead{count === 1 ? '' : 's'}
                </span>
              </button>
            );
          })}
          {looseLeads.length > 0 && (!walletQuery || 'sem carteira'.includes(walletQuery)) && (
            <button type="button" className="prospecting-wallet-card is-loose" onClick={() => openWallet('loose')}>
              <div className="prospecting-wallet-loose-icon"><Wallet size={22} /></div>
              <div className="prospecting-wallet-info">
                <strong>Sem carteira</strong>
                <span>Leads sem responsável com grupo ativo</span>
              </div>
              <span className="prospecting-wallet-count">{looseLeads.length} lead{looseLeads.length === 1 ? '' : 's'}</span>
            </button>
          )}
          {visibleHolders.length === 0 && looseLeads.length === 0 && (
            <p className="prospecting-wallet-empty">Nenhuma carteira encontrada.</p>
          )}
        </div>
      )}

      {openDropdownId && menuPos && createPortal(
        <div
          ref={dropdownRef}
          className="prospecting-dropdown-menu is-floating"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <div className="prospecting-dropdown-menu-content">
            <button type="button" onClick={() => navigate(`/erp/prospecting/proposals/new?lead_id=${openDropdownId}`)} className="prospecting-dropdown-item primary">
              <FileText size={14} /> Nova Proposta
            </button>
            <button type="button" onClick={() => navigate(`/erp/prospecting/leads/${openDropdownId}/proposals`)} className="prospecting-dropdown-item">
              <History size={14} /> Propostas da Lead
            </button>
            {hasPermission(MODULES.PROSPECTING, ACTIONS.PROSPECTING_UPDATE) && (
              <>
                <div className="prospecting-dropdown-divider" />
                <button
                  type="button"
                  onClick={() => {
                    const lead = leads.find((item) => item.id === openDropdownId);
                    if (lead) openCancelModal(lead);
                  }}
                  className="prospecting-dropdown-item danger"
                >
                  <XCircle size={14} /> Cancelar Lead
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Modal de redistribuição */}
      {isRedistributeOpen && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => !isSaving && setIsRedistributeOpen(false)} />
          <div className="prospecting-modal-content">
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper"><Users size={24} /></div>
                <div>
                  <h3 className="prospecting-modal-title">Redistribuir carteira</h3>
                  <p className="prospecting-modal-subtitle">
                    {selectedLeadIds.length} lead{selectedLeadIds.length === 1 ? '' : 's'} · só grupos com carteira ativa
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setIsRedistributeOpen(false)} className="prospecting-modal-close"><X size={24} /></button>
            </div>
            <div className="prospecting-modal-body">
              <div className="prospecting-recipient-list">
                {holders.map((holder) => (
                  <label key={holder.id} className={`prospecting-recipient ${recipientIds.includes(Number(holder.id)) ? 'is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={recipientIds.includes(Number(holder.id))}
                      onChange={() => toggleRecipient(Number(holder.id))}
                    />
                    <Avatar name={holder.name} email={holder.email || ''} imageUrl={holder.avatar_url} size="sm" />
                    <span>
                      <strong>{holder.name}</strong>
                      <em>{holder.access_group_name || 'Grupo de acesso'}</em>
                    </span>
                  </label>
                ))}
              </div>
              {redistributeError ? <p className="prospecting-wallet-error">{redistributeError}</p> : null}
              <p className="patients-muted">Com mais de uma pessoa, as leads são repartidas em rodízio.</p>
            </div>
            <div className="prospecting-modal-footer">
              <button type="button" className="prospecting-btn" onClick={() => setIsRedistributeOpen(false)}>Cancelar</button>
              <button type="button" className="prospecting-btn prospecting-btn-primary" disabled={isSaving || !recipientIds.length} onClick={handleRedistribute}>
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Registro de Comunicação */}
      {isInteractionModalOpen && activeLead && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => !isSaving && setIsInteractionModalOpen(false)} />
          <div className="prospecting-modal-content">
             {saveSuccess ? (
               <div className="prospecting-modal-success">
                  <div className="prospecting-modal-success-icon">
                     <CheckCircle2 size={40} />
                  </div>
                  <div>
                    <h3 className="prospecting-modal-success-title">Registro Concluído</h3>
                    <p className="prospecting-modal-success-message">O histórico do lead foi atualizado com sucesso.</p>
                  </div>
               </div>
             ) : (
               <form onSubmit={handleSaveInteraction}>
                  <div className="prospecting-modal-header">
                     <div className="prospecting-modal-header-content">
                        <div className="prospecting-modal-icon-wrapper">
                           {interactionType === 'phone' ? <Phone size={24} /> : interactionType === 'message' ? <MessageSquare size={24} /> : <Mail size={24} />}
                        </div>
                        <div>
                           <h3 className="prospecting-modal-title">Registrar Contato</h3>
                           <p className="prospecting-modal-subtitle">Feedback com {activeLead.name}</p>
                        </div>
                     </div>
                     <button type="button" onClick={() => setIsInteractionModalOpen(false)} className="prospecting-modal-close"><X size={24} /></button>
                  </div>

                  <div className="prospecting-modal-body">
                     <div className="prospecting-form-group">
                        <label className="prospecting-form-label">Descrição da Conversa</label>
                        <textarea 
                          required
                          value={interactionNote}
                          onChange={(e) => setInteractionNote(e.target.value)}
                          rows={4}
                          placeholder="Como foi o contato? O cliente demonstrou interesse? Quais os próximos passos?"
                          className="prospecting-form-textarea"
                        />
                     </div>
                     <div className="prospecting-satisfaction-group">
                        <p className="prospecting-satisfaction-label">Satisfação Percebida</p>
                        <div className="prospecting-satisfaction-buttons">
                           <button 
                             type="button" 
                             className={`prospecting-satisfaction-btn ${satisfaction === 'positive' ? 'active' : ''}`}
                             onClick={() => setSatisfaction('positive')}
                           >
                             <Smile size={20} />
                           </button>
                           <button 
                             type="button" 
                             className={`prospecting-satisfaction-btn ${satisfaction === 'neutral' ? 'active' : ''}`}
                             onClick={() => setSatisfaction('neutral')}
                           >
                             <Meh size={20} />
                           </button>
                           <button 
                             type="button" 
                             className={`prospecting-satisfaction-btn ${satisfaction === 'negative' ? 'active' : ''}`}
                             onClick={() => setSatisfaction('negative')}
                           >
                             <Frown size={20} />
                           </button>
                        </div>
                     </div>
                  </div>

                  <div className="prospecting-modal-footer">
                     <button disabled={isSaving} className="prospecting-btn prospecting-btn-primary">
                        {isSaving ? <Loader2 size={18} /> : <Save size={18} />} Salvar Registro
                     </button>
                  </div>
               </form>
             )}
          </div>
        </div>
      )}

      {/* Modal de Novo Lead */}
      {isAddLeadModalOpen && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => !isSaving && setIsAddLeadModalOpen(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '36rem' }}>
             <form onSubmit={handleAddLead}>
                <div className="prospecting-modal-header">
                   <div className="prospecting-modal-header-content">
                      <div className="prospecting-modal-icon-wrapper">
                         <UserPlus size={24} />
                      </div>
                      <div>
                         <h3 className="prospecting-modal-title">Novo Prospecto</h3>
                         <p className="prospecting-modal-subtitle">Cadastro Manual de Lead</p>
                      </div>
                   </div>
                   <button 
                     type="button" 
                     onClick={() => {
                       setIsAddLeadModalOpen(false);
                       setLeadFormData({
                         name: '',
                         institution: '',
                         specialty: '',
                         cpf_cnpj: '',
                         email: '',
                         phone: '',
                         cellphone: ''
                       });
                       setCustomerFound(null);
                     }} 
                     className="prospecting-modal-close"
                   >
                     <X size={24} />
                   </button>
                </div>
                <div className="prospecting-modal-body">
                   {customerFound && (
                     <div className="prospecting-customer-found">
                       ✓ Cliente encontrado: {customerFound.name}
                     </div>
                   )}
                   <div className="prospecting-form-grid">
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">CPF/CNPJ</label>
                         <input 
                           name="cpf_cnpj" 
                           type="text" 
                           className="prospecting-form-input" 
                           placeholder="000.000.000-00 ou 00.000.000/0000-00"
                           value={leadFormData.cpf_cnpj}
                           onChange={(e) => {
                             let value = e.target.value.replace(/\D/g, '');
                             // Formatar CPF ou CNPJ
                             if (value.length <= 11) {
                               value = value.replace(/(\d{3})(\d)/, '$1.$2');
                               value = value.replace(/(\d{3})(\d)/, '$1.$2');
                               value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                             } else {
                               value = value.replace(/^(\d{2})(\d)/, '$1.$2');
                               value = value.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
                               value = value.replace(/\.(\d{3})(\d)/, '.$1/$2');
                               value = value.replace(/(\d{4})(\d)/, '$1-$2');
                             }
                             setLeadFormData(prev => ({ ...prev, cpf_cnpj: value }));
                           }}
                         />
                         {searchingCustomer && (
                           <span className="prospecting-search-customer-status">
                             Buscando cliente...
                           </span>
                         )}
                      </div>
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">Nome do Lead</label>
                         <input 
                           name="name" 
                           required 
                           type="text" 
                           className="prospecting-form-input" 
                           placeholder="Ex: Dr. João Pedro"
                           value={leadFormData.name}
                           onChange={(e) => setLeadFormData(prev => ({ ...prev, name: e.target.value }))}
                         />
                      </div>
                   </div>
                   <div className="prospecting-form-grid">
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">Instituição</label>
                         <input 
                           name="institution" 
                           required 
                           type="text" 
                           className="prospecting-form-input" 
                           placeholder="Ex: Hospital das Clínicas"
                           value={leadFormData.institution}
                           onChange={(e) => setLeadFormData(prev => ({ ...prev, institution: e.target.value }))}
                         />
                      </div>
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">Setor</label>
                         <button
                           type="button"
                           className="prospecting-form-input prospecting-sector-trigger"
                           onClick={() => { setSectorQuery(''); setSectorOpen(true); }}
                         >
                           <span>{leadFormData.specialty || 'Buscar setor'}</span>
                           <ChevronDown size={16} />
                         </button>
                         <input type="hidden" name="specialty" value={leadFormData.specialty} />
                      </div>
                   </div>
                   <div className="prospecting-form-grid">
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">E-mail</label>
                         <input 
                           name="email" 
                           type="email" 
                           className="prospecting-form-input" 
                           placeholder="email@exemplo.com"
                           value={leadFormData.email}
                           onChange={(e) => setLeadFormData(prev => ({ ...prev, email: e.target.value }))}
                         />
                      </div>
                      <div className="prospecting-form-group">
                         <label className="prospecting-form-label">Telefone</label>
                         <input 
                           name="phone" 
                           type="tel" 
                           className="prospecting-form-input" 
                           placeholder="(00) 0000-0000"
                           value={leadFormData.phone}
                           onChange={(e) => {
                             let value = e.target.value.replace(/\D/g, '');
                             if (value.length <= 10) {
                               value = value.replace(/(\d{2})(\d)/, '($1) $2');
                               value = value.replace(/(\d{4})(\d)/, '$1-$2');
                             }
                             setLeadFormData(prev => ({ ...prev, phone: value }));
                           }}
                         />
                      </div>
                   </div>
                   <div className="prospecting-form-group">
                      <label className="prospecting-form-label">Celular</label>
                      <input 
                        name="cellphone" 
                        type="tel" 
                        className="prospecting-form-input" 
                        placeholder="(00) 00000-0000"
                        value={leadFormData.cellphone}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 11) {
                            value = value.replace(/(\d{2})(\d)/, '($1) $2');
                            value = value.replace(/(\d{5})(\d)/, '$1-$2');
                          }
                          setLeadFormData(prev => ({ ...prev, cellphone: value }));
                        }}
                      />
                   </div>
                </div>
                <div className="prospecting-modal-footer">
                   <button type="submit" disabled={isSaving} className="prospecting-btn prospecting-btn-primary">
                     {isSaving ? <Loader2 size={18} /> : <Save size={18} />} Cadastrar Oportunidade
                   </button>
                </div>
             </form>
          </div>
        </div>
      )}

      {/* Modal de Cancelar Lead */}
      {isCancelLeadModalOpen && leadToCancel && (
        <div className="prospecting-modal-overlay">
          <div className="prospecting-modal-backdrop" onClick={() => !isSaving && setIsCancelLeadModalOpen(false)} />
          <div className="prospecting-modal-content" style={{ maxWidth: '28rem' }}>
            <div className="prospecting-modal-header">
              <div className="prospecting-modal-header-content">
                <div className="prospecting-modal-icon-wrapper" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                  <XCircle size={24} />
                </div>
                <div>
                  <h3 className="prospecting-modal-title">Cancelar Lead</h3>
                  <p className="prospecting-modal-subtitle">Esta ação não pode ser desfeita</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => {
                  setIsCancelLeadModalOpen(false);
                  setLeadToCancel(null);
                }} 
                className="prospecting-modal-close"
                disabled={isSaving}
              >
                <X size={24} />
              </button>
            </div>
            <div className="prospecting-modal-body">
              <p style={{ 
                fontSize: '0.875rem', 
                color: 'var(--text-secondary, #6b7280)',
                lineHeight: '1.5',
                marginBottom: '1rem'
              }}>
                Tem certeza que deseja cancelar o lead <strong>{leadToCancel.name}</strong>?
              </p>
              <p style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-secondary, #6b7280)',
                lineHeight: '1.5'
              }}>
                O lead será marcado como cancelado e não aparecerá mais na lista ativa.
              </p>
            </div>
            <div className="prospecting-modal-footer">
              <button 
                type="button"
                onClick={() => {
                  setIsCancelLeadModalOpen(false);
                  setLeadToCancel(null);
                }}
                disabled={isSaving}
                className="prospecting-btn"
                style={{ 
                  background: 'var(--bg-secondary, #f9fafb)',
                  color: 'var(--text-primary, #111827)'
                }}
              >
                Não, manter ativo
              </button>
              <button 
                type="button"
                onClick={handleCancelLead}
                disabled={isSaving}
                className="prospecting-btn"
                style={{ 
                  background: '#ef4444',
                  color: 'white',
                  boxShadow: '0 20px 25px -5px rgba(239, 68, 68, 0.3)'
                }}
              >
                {isSaving ? <Loader2 size={18} /> : <XCircle size={18} />} Sim, cancelar lead
              </button>
            </div>
          </div>
        </div>
      )}

      <ERPSelectSearchModal
        isOpen={sectorOpen}
        onClose={() => setSectorOpen(false)}
        title="Setor"
        subtitle="Escolha o segmento do lead"
        searchPlaceholder="Buscar setor..."
        searchTerm={sectorQuery}
        onSearchChange={setSectorQuery}
        icon={<Search size={18} />}
      >
        {LEAD_SECTORS.filter((sector) => sector.toLowerCase().includes(sectorQuery.trim().toLowerCase())).map((sector) => (
          <ERPSelectModalItem
            key={sector}
            name={sector}
            selected={leadFormData.specialty === sector}
            onClick={() => {
              setLeadFormData((prev) => ({ ...prev, specialty: sector }));
              setSectorOpen(false);
              setSectorQuery('');
            }}
          />
        ))}
      </ERPSelectSearchModal>
    </div>
  );
};

export default ProspectingPage;
